import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

export default function VerifyEmail() {
  const { user, resendConfirmationEmail } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // Pull email from URL param
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const e = p.get('email');
    if (e) setEmail(e);
  }, []);

  // If user becomes authenticated (clicked the link), send them home
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleResend = async () => {
    if (!email) {
      showToast('No email address found. Please sign up again.', 'error');
      return;
    }
    setResending(true);
    const { error } = await resendConfirmationEmail(email);
    setResending(false);
    if (error) {
      showToast(error.message || 'Failed to resend. Try again shortly.', 'error');
    } else {
      setResent(true);
      showToast('Confirmation email sent!', 'success');
    }
  };

  return (
    <div className="auth-page verify-email-page">
      <div className="verify-card">
        {/* Animated envelope icon */}
        <div className="verify-icon">
          <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="14" width="56" height="38" rx="5" fill="var(--color-primary)" opacity="0.12" />
            <rect x="4" y="14" width="56" height="38" rx="5" stroke="var(--color-primary)" strokeWidth="2.5" />
            <path d="M4 20l28 20 28-20" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {/* notification dot */}
            <circle cx="50" cy="16" r="8" fill="var(--color-accent)" />
            <path d="M50 12v5M50 19v1" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="verify-title">Check your inbox</h1>

        <p className="verify-body">
          We sent a confirmation link to{' '}
          {email ? (
            <strong className="verify-email-highlight">{email}</strong>
          ) : (
            'your email address'
          )}.
          <br />
          Click the link to verify and you'll be signed in automatically.
        </p>

        <div className="verify-steps">
          <div className="verify-step">
            <div className="verify-step-num">1</div>
            <span>Open the email from <strong>IssueTracker</strong></span>
          </div>
          <div className="verify-step">
            <div className="verify-step-num">2</div>
            <span>Click the <strong>Confirm your email</strong> button</span>
          </div>
          <div className="verify-step">
            <div className="verify-step-num">3</div>
            <span>You'll be signed in and ready to go</span>
          </div>
        </div>

        <div className="verify-divider" />

        <p className="verify-resend-label">Didn't receive it? Check your spam folder or resend below.</p>

        <button
          className={`btn btn-secondary btn-full ${resent ? 'verify-resent' : ''}`}
          onClick={handleResend}
          disabled={resending || resent}
        >
          {resending ? (
            <><span className="btn-spinner" /> Sending…</>
          ) : resent ? (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Email sent!
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
              Resend confirmation email
            </>
          )}
        </button>

        <p className="auth-switch" style={{ marginTop: 20 }}>
          Wrong account?{' '}
          <a href="/signup" className="auth-link">Sign up with a different email</a>
        </p>
      </div>
    </div>
  );
}
